<script setup lang="ts">
import { scrollToAnchor } from '~/utilities/helpers/scrollToAnchor';
import { useMainStore } from '~/store/main.store';

const isOpen = ref(false);
const mainStore = useMainStore();

function toggleMenu() {
  isOpen.value = !isOpen.value;
}

const scrollToSection = (sectionId: string) => {
  scrollToAnchor(sectionId, 70);
  isOpen.value = false;
};

const stretchHeader = computed(() => mainStore.stretchHeader);
</script>

<template>
  <header class="app-header">
    <nav class="header" :class="{ 'is-stretched': stretchHeader }">
      <ul>
        <li>
          <nuxt-link to="/#mission" class="header__link font-links">
            Mission
          </nuxt-link>
        </li>
        <li>
          <nuxt-link to="/#products" class="header__link font-links">
            Live Products
          </nuxt-link>
        </li>

        <li>
          <nuxt-link to="/" class="header__logo">
            <icon size="20px" name="icons:logo3" />
          </nuxt-link>
        </li>

        <li>
          <nuxt-link to="/#team" class="header__link font-links">
            Team
          </nuxt-link>
        </li>

        <li>
          <nuxt-link
            to="/#join"
            class="header__link header__link--join font-links"
          >
            Join Switchcase
          </nuxt-link>
        </li>
      </ul>
    </nav>
    <div class="header-mobile" :class="{ 'is-open': isOpen }">
      <nuxt-link to="/" class="header__logo" @click="isOpen = false">
        <icon size="24px" name="icons:logo3" />
      </nuxt-link>
      <button class="header__menu-toggle" @click="toggleMenu">
        <transition name="fade" mode="out-in">
          <icon v-if="!isOpen" size="32px" name="icons:menu" />
          <icon v-else size="32px" name="icons:close" />
        </transition>
      </button>
      <transition name="fade">
        <div v-if="isOpen" class="header-mobile__menu">
          <ul>
            <li>
              <nuxt-link
                class="header__link--mobile font-links"
                @click="scrollToSection('mission')"
              >
                Mission
              </nuxt-link>
            </li>
            <li>
              <nuxt-link
                class="header__link--mobile font-links"
                @click="scrollToSection('products')"
              >
                Live Products
              </nuxt-link>
            </li>

            <li>
              <nuxt-link
                class="header__link--mobile font-links"
                @click="scrollToSection('team')"
              >
                Team
              </nuxt-link>
            </li>

            <li>
              <nuxt-link
                class="header__link header__link--mobile header__link--join font-links"
                @click="scrollToSection('join')"
              >
                Join Switchcase
              </nuxt-link>
            </li>
          </ul>
        </div>
      </transition>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  position: fixed;
  z-index: var(--layer-header);
  top: 20px;
  left: 0;
  width: 100%;

  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header {
  width: 100%;
  max-width: 177px;
  display: flex;
  height: 52px;
  padding: 13px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  background-color: var(--white);
  box-shadow: var(--shadow-main);
  transition: all 0.3s ease;
}

.header ul {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.header__logo {
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.header__logo span {
  cursor: pointer;
}

.header__link {
  visibility: hidden;
  opacity: 0;
  text-decoration: none;
  text-transform: uppercase;
  color: var(--white);
  white-space: nowrap;
  transition: visibility 0.1s ease, opacity 0.1s ease;
  padding: 4px;
}

.header__link--join {
  color: var(--main-blue);
}

.header:hover, .is-stretched {
  max-width: 592px;
}

.header:hover .header__link, .is-stretched .header__link {
  visibility: visible;
  opacity: 1;
  color: var(--black);
  transition: visibility 0.5s ease, opacity 0.5s ease,
    background-color 0.3s ease, color 0.3s ease;
}

.header:hover .header__link:hover, .is-stretched .header__link:hover {
  color: var(--white);
  background-color: var(--main-blue);
}

.header:hover .header__link--join, .is-stretched .header__link--join {
  color: var(--main-blue);
}

.header:hover .header__logo, .is-stretched .header__logo {
  margin: 0 8px;
}

.header-mobile {
  display: none;
}

.header__menu-toggle {
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.header-mobile__menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--white);
  z-index: var(--layer-header);
  padding: 12px 16px 16px;
  border-radius: 0 0 12px 12px;
}

.header-mobile__menu ul {
  width: 100%;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 27px;
}

.header__link--mobile {
  width: 100%;
  color: var(--black);
  text-decoration: none;
  text-transform: uppercase;
}

.header__link--mobile.header__link--join {
  opacity: 1;
  visibility: visible;
  color: var(--main-blue);
}

@media (max-width: 743px) {
  .app-header {
    left: 20px;
    width: calc(100vw - 40px);
    height: 64px;
  }

  .header {
    display: none;
  }

  .header-mobile {
    position: relative;
    width: 100%;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    border-radius: 12px;
    background: var(--white);
    box-shadow: var(--shadow-main);
    z-index: var(--layer-header);
    transition: border-radius 0.3s ease;
  }

  .header-mobile.is-open {
    border-radius: 12px 12px 0 0;
  }
}
</style>
